import { Seo } from "../components/Seo";
import { PageHero, CtaLink } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { FinalCta } from "../components/FinalCta";
import { BUSINESS } from "../content";
import { Building2, Store, Home, HeartPulse, Landmark } from "lucide-react";
import { track } from "../lib/track";

const CLIENTS = [
  { icon: Building2, label: "Offices" },
  { icon: Store, label: "Small Businesses" },
  { icon: Landmark, label: "Real Estate Offices" },
  { icon: HeartPulse, label: "Care Environments" },
  { icon: Home, label: "Commercial Facilities" },
];

export const CommercialPage = () => (
  <>
    <Seo
      title="Commercial Cleaning Melbourne South East | Pure Bliss Clean"
      description="Reliable ongoing commercial cleaning for offices, small businesses, real estate offices and care environments across Melbourne's South East — Carrum Downs, Dandenong, Frankston and surrounds. Request a free quote."
      path="/commercial-cleaning"
    />
    <PageHero
      eyebrow="Commercial Cleaning"
      titleLines={["Professional", "Commercial Cleaning"]}
      intro={`Keep your workplace clean, presentable and welcoming with reliable commercial cleaning tailored around your business, across ${BUSINESS.region}.`}
      image="https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxwcmlzdGluZSUyMGNsZWFuJTIwY29tbWVyY2lhbCUyMG9mZmljZSUyMHNwYWNlfGVufDB8fHx8MTc4NzAyNTEyOHww&ixlib=rb-4.1.0&q=85"
      imageAlt="Pristine, freshly cleaned commercial office space"
    >
      <Reveal delay={0.4}>
        <div className="mt-8">
          <CtaLink to="#quote" testId="commercial-page-cta" onClick={() => track("cta_click", { label: "commercial_page" })}>Request a Commercial Cleaning Quote</CtaLink>
        </div>
      </Reveal>
    </PageHero>

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <Eyebrow>Ongoing & Recurring</Eyebrow>
          <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">Cleaning Your Business Can Rely On</h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">
            Our commercial focus is ongoing, recurring work — consistent cleaning on a schedule that suits your
            business, with the same attention to detail we bring to every home we clean.
          </p>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">
            We already provide ongoing scheduled cleaning services across residential, real estate and care
            environments, including regular weekly arrangements.
          </p>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2">
          {CLIENTS.map((c, i) => (
            <Reveal key={c.label} delay={0.07 * i} className={i === 4 ? "sm:col-span-2" : ""}>
              <div className="flex h-full items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lg">
                <span className="rounded-2xl bg-teal-50 p-3 text-brand-teal"><c.icon className="h-5 w-5" /></span>
                <span className="font-display text-base font-bold text-brand-ink">{c.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-brand-mist">
      <div className="mx-auto max-w-3xl px-6 py-24 lg:py-32">
        <Reveal className="mb-10 text-center">
          <div className="flex justify-center"><Eyebrow>Free Quote</Eyebrow></div>
          <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">Request a Commercial Quote</h2>
          <p className="mx-auto mt-4 max-w-md text-slate-600">Tell us about your workplace and how often you need cleaning, and we'll be in touch.</p>
        </Reveal>
        <div id="quote">
          <QuoteForm defaultService="Commercial Cleaning" source="commercial_page" />
        </div>
      </div>
    </section>
    <FinalCta />
  </>
);
